/**
 * One figure, in plain words: what it is, the workbook cell it was read from
 * with that cell's formula, and how it sits next to the plat's other houses.
 * The loan tape's column panel, for a cell.
 */

import { ExternalLink, X } from "lucide-react";
import React from "react";
import type { Portfolio, PortfolioFact } from "../../services/portfolioDataService";
import {
	ORIGIN_LABEL,
	ORIGIN_OF_BASIS,
	ORIGIN_PILL,
	formatMonth,
	formatMoney,
	inWords,
	lineItemDefinition,
	median,
	workbookCellHref,
} from "./portfolioModel";

export interface CellPanelProps {
	portfolio: Portfolio;
	fact: PortfolioFact;
	label: string;
	onClose: () => void;
	onAsk: () => void;
}

/** The lines a figure is read next to: what it is made of, or compared with. */
const RELATED: Record<string, { item: string; label: string }[]> = {
	rent_collected: [
		{ item: "rent_billed", label: "Rent billed" },
		{ item: "vacancy_loss", label: "Shortfall (billed − collected)" },
	],
	rent_billed: [
		{ item: "rent_collected", label: "Rent collected" },
		{ item: "vacancy_loss", label: "Shortfall (billed − collected)" },
	],
	vacancy_loss: [
		{ item: "rent_billed", label: "Rent billed" },
		{ item: "rent_collected", label: "Rent collected" },
	],
	net_income: [
		{ item: "rent_collected", label: "Rent collected" },
		{ item: "landlord_nonreimbursable_expenses", label: "Non-reimbursable repairs" },
		{ item: "property_taxes", label: "Property taxes" },
		{ item: "interest_expense", label: "Interest (split)" },
	],
	dcr: [
		{ item: "net_income", label: "Net income" },
		{ item: "interest_expense", label: "Interest (split)" },
		{ item: "principal_paid", label: "Principal (split)" },
	],
	net_after_principal_and_capex: [
		{ item: "net_income", label: "Net income" },
		{ item: "replacement_costs", label: "Replacement costs" },
		{ item: "principal_paid", label: "Principal (split)" },
	],
};

const SECTION_HEADING =
	"text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]";

const CellPanel: React.FC<CellPanelProps> = ({ portfolio, fact, label, onClose, onAsk }) => {
	const origin = ORIGIN_OF_BASIS[fact.basis];
	const house = portfolio.tables.house.find((h) => h.house_id === fact.house_id);
	const definition = inWords(lineItemDefinition(portfolio, fact.line_item));
	const href = workbookCellHref(portfolio, fact);
	const isRatio = fact.unit === "ratio";
	const format = (value: number | null) =>
		value === null ? "—" : isRatio ? value.toFixed(2) : `$${formatMoney(value)}`;

	const neighbours = portfolio.tables.fact.filter(
		(f) => f.plat_id === fact.plat_id && f.year === fact.year && f.line_item === fact.line_item,
	);
	const values = neighbours.map((f) => f.value).filter((v): v is number => v !== null);
	const mid = median(values);
	const rank =
		fact.value === null
			? null
			: [...values].sort((a, b) => b - a).indexOf(fact.value) + 1;
	const platName = String(house?.plat_name ?? fact.plat_id);
	const sameHouse = (item: string) =>
		portfolio.tables.fact.find(
			(f) => f.house_id === fact.house_id && f.year === fact.year && f.line_item === item,
		) ?? null;
	const related = (RELATED[fact.line_item] ?? []).flatMap(({ item, label: name }) => {
		const other = sameHouse(item);
		return other ? [{ label: name, fact: other }] : [];
	});
	const billed = sameHouse("rent_billed")?.value ?? null;
	const loss = sameHouse("vacancy_loss")?.value ?? null;
	const shortfallShare = billed && loss !== null ? loss / billed : null;
	const formula = fact.formula && fact.formula !== "nan" ? fact.formula : "";

	return (
		<aside
			aria-label="About this figure"
			className="flex w-[420px] flex-shrink-0 flex-col border-l border-gray-200 bg-white shadow-[-12px_0_32px_rgba(17,24,39,0.06)]"
		>
			<div className="flex items-start gap-3 border-b border-[#f1f2f4] px-[22px] pb-4 pt-5">
				<div className="min-w-0 flex-1">
					<p className="text-xs text-[#6B7280]">
						{fact.house_id}
						{house?.address ? ` · ${String(house.address)}` : ""}
					</p>
					<h2 className="mt-1 break-words text-xl font-semibold text-[#111111]">
						{label}, {fact.year}
					</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close figure"
					className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5B6472] hover:bg-gray-100"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[22px] py-[18px]">
				<div className="flex flex-wrap items-baseline gap-3">
					<span className="text-[28px] font-semibold tabular-nums text-[#111111]">
						{format(fact.value)}
					</span>
					<span
						className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${ORIGIN_PILL[origin]}`}
					>
						{ORIGIN_LABEL[origin]}
					</span>
				</div>

				{definition && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>In plain words</h3>
						<p className="text-[15px] leading-relaxed text-[#1F2937]">{definition}</p>
					</section>
				)}

				<section className="flex flex-col gap-2">
					<h3 className={SECTION_HEADING}>Where it's from in your workbook</h3>
					<div className="flex flex-col gap-1.5 rounded-[10px] border border-[#eceae6] bg-[#f7f7f6] px-3 py-2.5">
						<span className="font-mono text-[12.5px] text-[#1F2937]">
							{fact.sheet}!{fact.cell}
						</span>
						<span className="text-[12.5px] text-[#5B6472]">“{fact.source_label.trim()}”</span>
						{formula && (
							<span className="break-all font-mono text-[12px] text-[#5B6472]">{formula}</span>
						)}
						<span className="text-xs text-[#6B7280]">{fact.source_file}</span>
					</div>
					{href ? (
						<a
							href={href}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 self-start py-1 text-[13px] font-medium text-[#004C4B] underline underline-offset-[3px]"
						>
							Open in workbook
							<ExternalLink className="h-3.5 w-3.5" aria-hidden />
						</a>
					) : (
						<span className="text-xs text-[#6B7280]">
							This workbook isn't in the Deal Room, so it can't be opened here.
						</span>
					)}
				</section>

				{related.length > 0 && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>Next to it for {fact.house_id}</h3>
						<dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[13px]">
							{related.map(({ label: name, fact: other }) => (
								<React.Fragment key={other.line_item}>
									<dt className="text-[#5B6472]">
										{name} <span className="font-mono text-[11.5px] text-[#9a9ea3]">{other.cell}</span>
									</dt>
									<dd
										className={`text-right tabular-nums ${(other.value ?? 0) < 0 || (other.line_item === "vacancy_loss" && shortfallShare !== null && shortfallShare >= 0.1) ? "text-[#b42318]" : "text-[#1F2937]"}`}
									>
										{format(other.value)}
										{other.line_item === "vacancy_loss" && shortfallShare !== null
											? ` · ${(shortfallShare * 100).toFixed(1)}%`
											: ""}
									</dd>
								</React.Fragment>
							))}
						</dl>
					</section>
				)}

				{values.length > 1 && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>Next to the rest of {platName}</h3>
						<dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[13px]">
							<dt className="text-[#5B6472]">Median of {values.length} houses</dt>
							<dd className="text-right tabular-nums text-[#1F2937]">{format(mid)}</dd>
							{rank !== null && rank > 0 && (
								<>
									<dt className="text-[#5B6472]">Rank, highest first</dt>
									<dd className="text-right tabular-nums text-[#1F2937]">
										{rank} of {values.length}
									</dd>
								</>
							)}
							{house?.lease_start && (
								<>
									<dt className="text-[#5B6472]">Lease started</dt>
									<dd className="text-right tabular-nums text-[#1F2937]">
										{formatMonth(String(house.lease_start))}
									</dd>
								</>
							)}
						</dl>
						{origin === "split" && (
							<p className="rounded-lg border border-[#fedf89] bg-[#fffaeb] px-3 py-2 text-[12.5px] leading-relaxed text-[#7a2e0e]">
								The same figure for every house in the plat: one plat amount divided
								evenly. Don't compare houses on it.
							</p>
						)}
					</section>
				)}

				<button
					type="button"
					onClick={onAsk}
					className="mt-auto inline-flex h-9 items-center self-start rounded-lg border border-[#99CFCD] bg-white px-3.5 text-sm font-medium text-[#004C4B] hover:bg-[#f6fbfb]"
				>
					Ask about this figure
				</button>
			</div>
		</aside>
	);
};

export default CellPanel;
